import { format, formatDistanceToNow, isValid } from "date-fns";

export function classNames(...parts) {
  return parts.filter(Boolean).join(" ");
}

export function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (!isValid(date)) return "—";
  return format(date, "MMM d, h:mm a");
}

export function formatDay(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (!isValid(date)) return "—";
  return format(date, "MMM d");
}

export function formatRelative(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (!isValid(date)) return "Never";
  return formatDistanceToNow(date, { addSuffix: true });
}

export function formatDueLabel(value) {
  if (!value) return "Unscheduled";
  const date = new Date(value);
  if (!isValid(date)) return "Unscheduled";
  const diffMs = date.getTime() - Date.now();
  const diffHours = Math.round(diffMs / (1000 * 60 * 60));
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffMs < 0) {
    const ago = Math.abs(diffHours);
    if (ago < 24) return `Overdue ${ago}h`;
    return `Overdue ${Math.abs(diffDays)}d`;
  }
  if (diffHours < 1) return "Due now";
  if (diffHours < 24) return `In ${diffHours}h`;
  if (diffDays < 14) return `In ${diffDays}d`;
  return format(date, "MMM d");
}

export function primaryContactEmail(lead) {
  if (!lead) return null;
  return (
    lead.primary_email ||
    lead.emails?.[0]?.address ||
    lead.people?.find((p) => p.email)?.email ||
    null
  );
}

export function stageTone(stage) {
  switch (stage) {
    case "replied":
    case "won":
    case "meeting_booked":
      return "success";
    case "qualified":
      return "warning";
    case "contacted":
    case "queued":
      return "info";
    case "lost":
    case "do_not_contact":
      return "danger";
    default:
      return "default";
  }
}

export function buildThreadMessages(detail) {
  if (!detail?.lead) return [];

  const fromActivities = (detail.activities || []).map((activity) => ({
    direction: activity.direction,
    from: activity.from,
    to: activity.to,
    subject: activity.subject,
    html: activity.html,
    text: activity.text,
    timestamp: activity.occurredAt,
    threadId: activity.threadId,
    messageId: activity.providerMessageId || activity._id,
  }));

  const fromLegacy = (detail.lead.messages || []).map((message) => ({
    ...message,
    timestamp: message.timestamp,
  }));

  const seen = new Set();
  return [...fromActivities, ...fromLegacy]
    .filter((message) => {
      const key =
        message.messageId ||
        `${message.direction}-${message.threadId}-${message.subject}-${message.timestamp}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
}

export function formatClockTime(hour, minute = 0) {
  const h = Number.isFinite(hour) ? hour : 9;
  const m = Number.isFinite(minute) ? minute : 0;
  const date = new Date();
  date.setHours(h, m, 0, 0);
  return format(date, "h:mm a");
}

// Human-readable cadence label for a sequence step, e.g.
//   "Day 0 · 9:00 AM"
//   "+3 business days · 10:30 AM"
//   "+7 days · 9:00 AM"
export function formatStepCadence(step, { isFirst = false } = {}) {
  if (!step) return "";
  const delay = Number.isFinite(step.delayDays) ? step.delayDays : 0;
  const unit = step.businessDaysOnly ? "business day" : "day";
  const offset =
    isFirst && delay === 0
      ? "Day 0"
      : delay === 1
      ? `+1 ${unit}`
      : `+${delay} ${unit}s`;
  const time = formatClockTime(step.sendAtHour, step.sendAtMinute);
  return `${offset} · ${time}`;
}

// -------------------- Template / merge-variable helpers --------------------
//
// Templates use {{var}} and {{var|fallback text}} syntax. The fallback is only
// applied when the resolved value is empty/whitespace. We render client-side
// so what the operator previews is exactly what ships — no reliance on the
// backend to fill in vars correctly.

const MERGE_TOKEN_RE = /\{\{\s*([a-zA-Z0-9_.]+)\s*(?:\|([^}]*))?\}\}/g;

// Build a plain key/value map from a lead + campaignLead + admin bundle. Keys
// are the usual IMPORT_FIELDS plus a handful of derived ones.
export function buildMergeVars({ lead, campaignLead, admin } = {}) {
  const person = lead?.people?.[0] || {};
  const firstName =
    lead?.first_name || person.first_name || splitName(lead?.owner_name).first || "";
  const lastName =
    lead?.last_name || person.last_name || splitName(lead?.owner_name).last || "";
  const ownerName = lead?.owner_name || [firstName, lastName].filter(Boolean).join(" ") || "";

  return {
    first_name: firstName,
    last_name: lastName,
    owner_name: ownerName,
    company_name: lead?.company_name || lead?.company || "",
    website: lead?.website || "",
    phone: lead?.phone || person.phone || "",
    email: primaryContactEmail(lead) || "",
    city: lead?.city || "",
    state: lead?.state || "",
    title: lead?.title || person.title || "",
    tags: Array.isArray(lead?.tags) ? lead.tags.join(", ") : lead?.tags || "",
    sender_first_name: admin?.firstName || "",
    sender_last_name: admin?.lastName || "",
    sender_full_name:
      [admin?.firstName, admin?.lastName].filter(Boolean).join(" ") || "",
    step_index: (campaignLead?.stepIndex ?? 0) + 1,
  };
}

function splitName(full) {
  if (!full) return { first: "", last: "" };
  const parts = String(full).trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}

export function renderTemplate(template, vars = {}) {
  if (!template) return "";
  return String(template).replace(MERGE_TOKEN_RE, (_match, key, fallback) => {
    const raw = vars?.[key];
    const value = raw === undefined || raw === null ? "" : String(raw).trim();
    if (value) return value;
    return (fallback ?? "").trim();
  });
}

// Return the list of merge-var keys that are still unresolved (empty value and
// no fallback provided). Used to warn before sending.
export function detectUnresolvedVars(template, vars = {}) {
  if (!template) return [];
  const unresolved = new Set();
  const re = new RegExp(MERGE_TOKEN_RE.source, "g");
  let match;
  while ((match = re.exec(String(template))) !== null) {
    const key = match[1];
    const fallback = (match[2] ?? "").trim();
    const raw = vars?.[key];
    const value = raw === undefined || raw === null ? "" : String(raw).trim();
    if (!value && !fallback) unresolved.add(key);
  }
  return Array.from(unresolved);
}

// List ALL merge-var keys referenced in a template, regardless of whether they
// are resolved or have fallbacks. Useful for editor hints.
export function listMergeVars(template) {
  if (!template) return [];
  const keys = new Set();
  const re = new RegExp(MERGE_TOKEN_RE.source, "g");
  let match;
  while ((match = re.exec(String(template))) !== null) keys.add(match[1]);
  return Array.from(keys);
}

export function getApiErrorMessage(error, fallback) {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}
